import React, { createContext, useContext, useState, useEffect } from 'react';
import { attachUtmToUser } from '../lib/utm';

const AuthContext = createContext();
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  // iter 144h - once auth resolves (sign-in, magic-link landing, or page
  // refresh with valid cookie) fire a single fire-and-forget PATCH to
  // attach the first-touch UTM block to the user record. Server-side
  // first-touch protection prevents overwrites, so this is safe to call
  // on every auth resolution.
  useEffect(() => {
    if (user && user.email) {
      attachUtmToUser({ email: user.email, useAuth: true });
    }
  }, [user]);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include',
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, rememberMe = false) => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, remember_me: !!rememberMe }),
    });

    // Defensive JSON parse: during deploy windows or a misrouted edge
    // request the response can occasionally be HTML (the SPA's
    // index.html). Without this guard `response.json()` throws
    // "Unexpected token '<', DOCTYPE is not valid JSON" - a hostile
    // error that misled users on production.
    const data = await response.json().catch(() => null);

    if (!response.ok) {
      const msg = (data && data.detail) || `Login failed (HTTP ${response.status})`;
      throw new Error(typeof msg === 'string' ? msg : 'Login failed');
    }
    if (!data) {
      throw new Error('Login service returned an unexpected response. Please try again in a moment.');
    }

    // Check if user needs to set password (migrated from Google)
    if (data.needs_password) {
      return { needsPassword: true, email: data.email };
    }

    // iter 85: persist the "Stay signed in" preference so a return visit
    // to /signin can decide whether to auto-redirect to the portal or
    // always show the login form (PortalLogin reads this key).
    try {
      if (typeof window !== 'undefined') {
        if (rememberMe) {
          window.localStorage.setItem('mundalo_remember_me', '1');
        } else {
          window.localStorage.removeItem('mundalo_remember_me');
        }
      }
    } catch (_) { /* swallow */ }

    setUser(data.user);
    return { success: true, user: data.user };
  };

  const signup = async (email, password, confirmPassword, firstName, lastName, consents = {}) => {
    const response = await fetch(`${API_URL}/api/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        email,
        password,
        confirm_password: confirmPassword,
        first_name: firstName,
        last_name: lastName,
        terms_accepted: !!consents.terms_accepted,
        email_consent: !!consents.email_consent,
      }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Signup failed');
    }
    
    return { success: true, message: data.message };
  };

  const forgotPassword = async (email) => {
    const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Request failed');
    }
    
    return { success: true, message: data.message };
  };

  const resetPassword = async (token, password, confirmPassword) => {
    const response = await fetch(`${API_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        token,
        password,
        confirm_password: confirmPassword,
      }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Reset failed');
    }
    
    setUser(data.user);
    return { success: true, user: data.user };
  };

  const completeSignupFromPayment = async (email, password, confirmPassword) => {
    const response = await fetch(`${API_URL}/api/auth/complete-signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        email,
        password,
        confirm_password: confirmPassword,
      }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Signup completion failed');
    }
    
    setUser(data.user);
    return { success: true, user: data.user };
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
    // Clear any client-side residue (legacy localStorage tokens, etc.) so we
    // can't be auto-re-signed by stale state on the next page load.
    // iter 85: also clear the `mundalo_remember_me` preference so a fresh
    // browser doesn't auto-redirect to the portal after this user signed out.
    try {
      if (typeof window !== 'undefined') {
        ['token', 'auth_token', 'access_token', 'mundalo_token', 'mundalo_user',
         'mundalo_remember_me'].forEach((k) => {
          try { window.localStorage.removeItem(k); } catch (_) {}
          try { window.sessionStorage.removeItem(k); } catch (_) {}
        });
      }
    } catch (_) {}
    setUser(null);
    // Hard reload to the public homepage (not a SPA navigation) so all
    // in-memory state is reset and no stale AuthContext can re-trigger.
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      loading,
      login,
      signup,
      forgotPassword,
      resetPassword,
      completeSignupFromPayment,
      logout,
      checkAuth,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
